import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  clearChunkReloadFlag,
  hasAlreadyReloadedForChunkError,
  isChunkLoadError,
  tryReloadOnceForChunkError,
} from '@/utils/chunk-load-error';

interface ErrorFallbackProps {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}

function ChunkUpdateFallback({ onRefresh }: { onRefresh: () => void }) {
  return (
    <div role="alert" className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="text-center max-w-md space-y-4">
        <h2 className="text-2xl font-semibold tracking-tight">Update available</h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          A newer version of Terrace Metrics is ready. Refresh to continue — your progress on this
          page may need to be re-entered.
        </p>
        <Button size="lg" onClick={onRefresh}>
          Refresh page
        </Button>
      </div>
    </div>
  );
}

function GenericErrorFallback({
  errorMessage,
  resetError,
}: {
  errorMessage: string;
  resetError: () => void;
}) {
  return (
    <div role="alert" className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="text-center max-w-md space-y-4">
        <h2 className="text-2xl font-semibold tracking-tight">Something went wrong</h2>
        <p className="text-muted-foreground text-sm">
          Please try again. If the problem continues, refresh the page or contact support.
        </p>
        <details className="text-left">
          <summary className="cursor-pointer text-sm text-muted-foreground mb-2">
            Error details
          </summary>
          <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-40">{errorMessage}</pre>
        </details>
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" onClick={() => window.location.reload()}>
            Refresh page
          </Button>
          <Button onClick={resetError}>Try again</Button>
        </div>
      </div>
    </div>
  );
}

export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    if (!chunkError) return;
    // Seamless recovery after deploy: reload once before showing any UI
    tryReloadOnceForChunkError();
  }, [chunkError]);

  if (chunkError) {
    // While the one-shot reload runs, show a calm interim message
    if (!hasAlreadyReloadedForChunkError()) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-background">
          <p className="text-sm text-muted-foreground">Loading the latest version…</p>
        </div>
      );
    }

    return (
      <ChunkUpdateFallback
        onRefresh={() => {
          clearChunkReloadFlag();
          window.location.reload();
        }}
      />
    );
  }

  const errorMessage = error instanceof Error ? error.message : String(error);
  return <GenericErrorFallback errorMessage={errorMessage} resetError={resetError} />;
}
